import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useSuccessAlert, useErrorAlert, useInfoAlert } from './AlertSystem';
import { useApiErrorHandler } from '../lib/errorHandling';
import './DashboardProfile.css';

interface UserProfile {
    id: string;
    name: string;
    email: string;
    role: string;
    profilePicture: string | null;
    createdAt: string;
    lastLogin: string;
}

const DashboardProfile: React.FC = () => {
    const { user } = useAuth();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [isEditing, setIsEditing] = useState(false);
    const [editedProfile, setEditedProfile] = useState<UserProfile | null>(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(true);

    // Notification states (dummy data for UI)
    const [notifications, setNotifications] = useState({
        email: false,
        sms: false,
        push: false,
        marketing: false
    });

  // Alert hooks
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  const showInfo = useInfoAlert();
  const { handleApiError } = useApiErrorHandler();    useEffect(() => {
        // Fetch user profile from API
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const response = await api.user.getProfile();

                if (response.success && response.data) {
                    const userData = response.data.user || response.data; // Handle both nested and direct user data

                    // Transform API data to component format
                    const profileData: UserProfile = {
                        id: userData.id?.toString() || '',
                        name: userData.name || '',
                        email: userData.email || '',
                        role: userData.role?.name || 'User',
                        profilePicture: userData.profilePicture || null,
                        createdAt: userData.createdAt || new Date().toISOString(),
                        lastLogin: userData.lastLogin || new Date().toISOString()
                    };

                    console.log('Loaded profile data:', profileData); // Debug log
                    setProfile(profileData);
                    setEditedProfile(profileData);
                } else {
                    // Fallback to current user data from auth
                    if (user) {
                        const fallbackProfile: UserProfile = {
                            id: user.id,
                            name: user.name || '',
                            email: user.email,
                            role: user.role?.name || 'User',
                            profilePicture: null,
                            createdAt: new Date().toISOString(),
                            lastLogin: new Date().toISOString()
                        };
                        setProfile(fallbackProfile);
                        setEditedProfile(fallbackProfile);
                    }
                }
            } catch (error) {
                console.error('Error fetching profile:', error);

                // Fallback to current user data from auth
                if (user) {
                    const fallbackProfile: UserProfile = {
                        id: user.id,
                        name: user.name || '',
                        email: user.email,
                        role: user.role?.name || 'User',
                        profilePicture: null,
                        createdAt: new Date().toISOString(),
                        lastLogin: new Date().toISOString()
                    };
                    setProfile(fallbackProfile);
                    setEditedProfile(fallbackProfile);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [user]);

    const handleProfileUpdate = async () => {
        if (!editedProfile) return;

        try {
            // Update profile via API using name directly
            const response = await api.user.updateProfile({
                name: editedProfile.name,
                email: editedProfile.email
            });

            if (response.success) {
                setProfile(editedProfile);
                setIsEditing(false);
                showSuccess('Profile updated successfully!', 'Success');
            } else {
                // Use the new error handling
                handleApiError(response, {
                    fieldErrorMap: {
                        name: 'Please enter a valid name (at least 2 characters)',
                        email: 'Please enter a valid email address'
                    }
                });
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            showError('Network error. Please check your connection and try again.', 'Connection Error');
        }
    };

    const handlePasswordChange = async () => {
        if (newPassword !== confirmPassword) {
            showError('Passwords do not match', 'Validation Error');
            return;
        }

        if (!currentPassword || !newPassword) {
            showError('Please fill in all password fields', 'Validation Error');
            return;
        }

        if (newPassword.length < 8) {
            showError('Password must be at least 8 characters long', 'Validation Error');
            return;
        }

        // Validate password strength according to backend requirements
        const hasLowercase = /[a-z]/.test(newPassword);
        const hasUppercase = /[A-Z]/.test(newPassword);
        const hasNumber = /\d/.test(newPassword);

        if (!hasLowercase || !hasUppercase || !hasNumber) {
            showError('Password must contain at least one lowercase letter, one uppercase letter, and one number', 'Password Requirements');
            return;
        }

        try {
            // Show info alert about the process
            showInfo('Updating your password...', 'Processing', { duration: 2000 });

            // Change password via API
            const response = await api.user.changePassword(currentPassword, newPassword);

            if (response.success) {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                showSuccess('Password changed successfully! Please remember your new password.', 'Security Update', { duration: 6000 });
            } else {
                // Use the new error handling
                handleApiError(response, {
                    fieldErrorMap: {
                        currentPassword: 'Current password is incorrect',
                        newPassword: 'Password must contain at least one lowercase letter, one uppercase letter, and one number',
                        password: 'Password must contain at least one lowercase letter, one uppercase letter, and one number'
                    }
                });
            }
        } catch (error) {
            console.error('Error changing password:', error);
            showError('Network error. Please check your connection and try again.', 'Connection Error');
        }
    };

    const handleNotificationChange = (key: string) => {
        // Toggle notification setting
        const wasEnabled = notifications[key as keyof typeof notifications];
        setNotifications(prev => ({
            ...prev,
            [key]: !prev[key as keyof typeof prev]
        }));

        const notificationTypes: Record<string, string> = {
            email: 'Email',
            sms: 'SMS',
            push: 'Push',
            marketing: 'Marketing'
        };

        const typeName = notificationTypes[key] || key;
        showSuccess(
            `${typeName} notifications ${wasEnabled ? 'disabled' : 'enabled'}`,
            'Notification Settings'
        );
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="dashboard-profile">
                    <div className="loading-state">
                        <i className="ri-loader-4-line"></i>
                        <span>Loading profile...</span>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (!profile) {
        return (
            <DashboardLayout>
                <div className="dashboard-profile">
                    <div className="error-state">
                        <i className="ri-error-warning-line"></i>
                        <span>Error loading profile</span>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="User Profile">
            <div className="dashboard-profile">
                <div className="profile-header">
                    <div className="profile-avatar">
                        <div className="avatar-placeholder">
                            <i className="ri-user-3-line"></i>
                        </div>
                    </div>
                    <div className="profile-info">
                        <h1>{profile.name}</h1>
                        <p className="profile-role">{profile.role}</p>
                        <p className="profile-email">{profile.email}</p>
                    </div>
                    <div className="profile-actions">
                        {!isEditing && (
                            <button className="btn-primary" onClick={() => setIsEditing(true)}>
                                <i className="ri-edit-line"></i>
                                Edit Profile
                            </button>
                        )}
                    </div>
                </div>

                <div className="profile-tabs">
                    <button
                        className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        <i className="ri-user-line"></i>
                        Profile
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        <i className="ri-shield-check-line"></i>
                        Security
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notifications')}
                    >
                        <i className="ri-notification-line"></i>
                        Notifications
                    </button>
                </div>

                <div className="profile-content">
                    {activeTab === 'profile' && (
                        <div className="profile-tab">
                            <div className="profile-section">
                                <h2>Personal Information</h2>
                                <div className="profile-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Name</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editedProfile?.name || ''}
                                                onChange={(e) => setEditedProfile(prev => prev ? { ...prev, name: e.target.value } : null)}
                                                disabled={!isEditing}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                className="form-input"
                                                value={editedProfile?.email || ''}
                                                onChange={(e) => setEditedProfile(prev => prev ? { ...prev, email: e.target.value } : null)}
                                                disabled={!isEditing}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Role</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editedProfile?.role || ''}
                                                disabled={true}
                                                readOnly
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Member Since</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={profile?.createdAt ? formatDate(profile.createdAt) : ''}
                                                disabled={true}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                                {isEditing && (
                                    <div className="form-actions">
                                        <button className="btn-secondary" onClick={() => {
                                            setIsEditing(false);
                                            setEditedProfile(profile);
                                        }}>
                                            Cancel
                                        </button>
                                        <button className="btn-primary" onClick={handleProfileUpdate}>
                                            Save Changes
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="security-tab">
                            <div className="profile-section">
                                <h2>Password</h2>
                                <div className="password-form">
                                    <div className="form-group">
                                        <label>Current Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="Enter current password"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>New Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                        />
                                        <div className="password-requirements">
                                            <small>Password must contain:</small>
                                            <ul>
                                                <li>At least 8 characters</li>
                                                <li>One lowercase letter (a-z)</li>
                                                <li>One uppercase letter (A-Z)</li>
                                                <li>One number (0-9)</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Confirm New Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                        />
                                    </div>
                                    <button className="btn-primary" onClick={handlePasswordChange}>
                                        Change Password
                                    </button>
                                </div>
                            </div>

                            <div className="profile-section">
                                <h2>Account Information</h2>
                                <div className="account-info">
                                    <div className="info-item">
                                        <span className="info-label">Last Login:</span>
                                        <span className="info-value">{formatDate(profile.lastLogin)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Account Created:</span>
                                        <span className="info-value">{formatDate(profile.createdAt)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Account Role:</span>
                                        <span className="info-value">{profile.role}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div className="notifications-tab">
                            <div className="profile-section">
                                <h2>Notification Preferences</h2>
                                <div className="notification-options">
                                    <div className="notification-option">
                                        <div className="option-info">
                                            <span className="option-label">Email Notifications</span>
                                            <span className="option-description">
                                                Receive security alerts and updates via email
                                            </span>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={notifications.email}
                                                onChange={() => handleNotificationChange('email')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="notification-option">
                                        <div className="option-info">
                                            <span className="option-label">SMS Notifications</span>
                                            <span className="option-description">
                                                Receive critical alerts via SMS
                                            </span>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={notifications.sms}
                                                onChange={() => handleNotificationChange('sms')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="notification-option">
                                        <div className="option-info">
                                            <span className="option-label">Push Notifications</span>
                                            <span className="option-description">
                                                Receive notifications in your browser
                                            </span>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={notifications.push}
                                                onChange={() => handleNotificationChange('push')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="notification-option">
                                        <div className="option-info">
                                            <span className="option-label">Marketing Communications</span>
                                            <span className="option-description">
                                                Receive product updates and promotional content
                                            </span>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={notifications.marketing}
                                                onChange={() => handleNotificationChange('marketing')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button className="btn-primary" onClick={() => showSuccess('Notification preferences saved!', 'Settings Updated')}>
                                        Save Preferences
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default DashboardProfile;
